import { cn, Skeleton, Tooltip } from '@heroui/react';

import { useContentHeaderContext } from './ContentHeaderContext';
import {
  RefreshIntervalSelector,
  type RefreshIntervalOption,
  type DefaultRefreshInterval
} from './RefreshIntervalSelector';

export type ContentHeaderTitleProps = {
  title: string;
  subtitle?: string;
  titleEndContent?: React.ReactNode;
  className?: string;
  hasRefreshInterval?: boolean;
  refreshIntervals?: RefreshIntervalOption[];
  refreshDefaultInterval?: DefaultRefreshInterval;
  onRefreshChange?: (interval: DefaultRefreshInterval) => void;
  dataTestId?: {
    title?: string;
    subtitle?: string;
  };
};

export function ContentHeaderTitle(props: ContentHeaderTitleProps) {
  const {
    title,
    subtitle,
    titleEndContent,
    className,
    hasRefreshInterval,
    refreshIntervals,
    refreshDefaultInterval,
    onRefreshChange,
    dataTestId
  } = props;
  const { isLoaded } = useContentHeaderContext('ContentHeaderTitle');

  if (!isLoaded) {
    return (
      <div className={cn('flex flex-col gap-0.5', className)}>
        <Skeleton className='h-6 w-[40rem] rounded-md' />
        <Skeleton className='h-5 w-[40rem] rounded-md' />
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col gap-0.5', className)}>
      <Tooltip
        content={
          <div className='max-w-5xl whitespace-normal break-words w-max'>
            {title}
          </div>
        }
        placement='top'
        size='md'
        isDisabled={!title || title.length < 50}
      >
        {titleEndContent ? (
          <div className='flex min-w-0 items-center gap-2'>
            <h2
              className='truncate text-title-md'
              data-testid={dataTestId?.title}
            >
              {title}
            </h2>
            {titleEndContent}
          </div>
        ) : (
          <h2 className='truncate text-title-md' data-testid={dataTestId?.title}>
            {title}
          </h2>
        )}
      </Tooltip>
      {subtitle && (
        <Tooltip
          content={
            <div className='max-w-5xl whitespace-normal break-words w-max'>
              {subtitle}
            </div>
          }
          placement='top'
          size='md'
          isDisabled={!subtitle || subtitle.length < 50}
        >
          <div className='flex items-center gap-2'>
            <span
              className='truncate text-body-regular-md'
              data-testid={dataTestId?.subtitle}
            >
              {subtitle}
            </span>
          </div>
        </Tooltip>
      )}
      {hasRefreshInterval && (
        <RefreshIntervalSelector
          onChange={onRefreshChange || (() => {})}
          intervals={refreshIntervals}
          defaultInterval={refreshDefaultInterval}
        />
      )}
    </div>
  );
}
